import { ReactNode, createContext, useContext, useState, useCallback } from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';

type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface Notification {
  id: string;
  type: NotificationType;
  header: string;
  content?: string;
  dismissible?: boolean;
}

interface NotificationContextType {
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setNotifications((prev) => [...prev, { ...notification, id }]);
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider value={{ addNotification, removeNotification, clearNotifications }}>
      {notifications.length > 0 && (
        <Column spacing="200" spacingInset="300">
          {notifications.map((n) => (
            <Alert
              key={n.id}
              type={n.type === 'info' ? 'neutral' : n.type}
              onClose={n.dismissible !== false ? () => removeNotification(n.id) : undefined}
            >
              {n.header}{n.content ? `: ${n.content}` : ''}
            </Alert>
          ))}
        </Column>
      )}
      {children}
    </NotificationContext.Provider>
  );
}
